import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { READINESS_BANDS } from "../../lib/scienceThresholds";

/**
 * ReadinessRing — Today screen hero atom.
 *
 * Renders the composite readiness score (0-100) as a circular ring
 * with tone derived from the score band. Animated via SVG
 * stroke-dashoffset transition — no JS RAF loop, no library, just
 * CSS-style transition on the path attribute.
 *
 * Tone bands (from IMPLEMENTATION_GUIDE_2026-06-20):
 *   <50  → red    (var(--danger-500))  — recover/skip
 *   50-69 → amber (var(--warning-500))  — caution
 *   70-84 → green (var(--ok))  — go
 *   85+  → bright (#5FF0A8)  — peaking
 *
 * Sub-chips (Sleep/HRV/RHR) are optional — when provided, paint a
 * tinted dot per signal so the athlete sees what's dragging the
 * score down without opening Wellness.
 */

export type ReadinessTone = "red" | "amber" | "green" | "bright" | "muted";

export interface ReadinessSubSignal {
  label: string;
  tone: ReadinessTone;
}

export interface ReadinessRingProps {
  score: number | null;        // 0-100; null → muted placeholder
  readout?: string;            // 1-line summary from compute_readiness
  subSignals?: ReadinessSubSignal[];
  size?: number;               // px diameter; default 180
  strokeWidth?: number;        // default 14
}

const TONE_COLOR: Record<ReadinessTone, string> = {
  red: "var(--danger-500)",
  amber: "var(--warning-500)",
  // Близнец TONE_COLOR из TodayHeroCard, поправленного в d6e519c9, — тот же
  // паттерн был пропущен здесь. Лайм снят с роли «хорошо» канон-сессией
  // 24.07; статус «ок» — okabe --ok, лайм остаётся у disc-run.
  green: "var(--ok)",
  bright: "var(--ok)",
  muted: "var(--fg-muted)",
};

const TRACK_COLOR = "var(--bg-divider)";

export function pickReadinessTone(score: number | null): ReadinessTone {
  if (score == null) return "muted";
  if (score >= READINESS_BANDS.bright) return "bright";
  if (score >= READINESS_BANDS.green) return "green";
  if (score >= READINESS_BANDS.amber) return "amber";
  return "red";
}

export function ReadinessRing({
  score,
  readout,
  subSignals,
  size = 180,
  strokeWidth = 14,
}: ReadinessRingProps) {
  const { t } = useTranslation("dashboard");
  const tone = useMemo(() => pickReadinessTone(score), [score]);
  const color = TONE_COLOR[tone];

  // SVG path math
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Fraction filled, clamped 0..1
  const fraction = score == null ? 0 : Math.max(0, Math.min(1, score / 100));
  const dashOffset = circumference * (1 - fraction);

  return (
    <div
      data-testid="readiness-ring"
      data-tone={tone}
      data-score={score ?? ""}
      className="inline-flex flex-col items-center gap-3"
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          role="img"
          aria-label={t("readiness.aria_label", {
            score: score ?? "—",
            defaultValue: `Готовность ${score ?? "—"} из 100`,
          })}
        >
          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={TRACK_COLOR}
            strokeWidth={strokeWidth}
            data-testid="readiness-ring-track"
          />
          {/* Progress arc — start at 12 o'clock, sweep clockwise */}
          {score != null && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${center} ${center})`}
              style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
              data-testid="readiness-ring-arc"
            />
          )}
        </svg>
        {/* Score label — font scales with ring size (compact hero = 76px) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div
            data-testid="readiness-ring-score"
            className="font-extrabold num-display tabular-nums leading-none"
            style={{ color, fontSize: Math.max(18, Math.round(size * 0.27)) }}
          >
            {score != null ? Math.round(score) : "—"}
          </div>
          {size >= 110 && (
            <div className="mt-1 text-[10px] uppercase tracking-wider text-fg-muted">
              {t("readiness.label", { defaultValue: "Готовность" })}
            </div>
          )}
        </div>
      </div>

      {readout && (
        <div
          data-testid="readiness-ring-readout"
          className="text-sm text-fg-secondary text-center max-w-[260px]"
        >
          {readout}
        </div>
      )}

      {subSignals && subSignals.length > 0 && (
        <div
          data-testid="readiness-ring-signals"
          className="flex items-center gap-3"
        >
          {subSignals.map((s) => (
            <div
              key={s.label}
              data-testid={`readiness-signal-${s.label}`}
              data-tone={s.tone}
              className="flex items-center gap-1.5"
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: TONE_COLOR[s.tone] }}
              />
              <span className="text-xs text-fg-secondary">{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
